import { z } from 'zod';

// ---- Request chat read-state (#570) ---------------------------------

/**
 * @aiPurpose hard cap on the number of request IDs the unread-summary
 *   endpoint accepts per call. Bounds the aggregate query and matches
 *   the issue's resolved decision so an all-scope actor cannot blow
 *   through a global scan by sending thousands of ids.
 * @aiInvariant exceeding the cap surfaces the standard
 *   `VALIDATION_ERROR` envelope at the API boundary (400).
 */
export const REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS = 200;

/**
 * @aiPurpose query string for
 *   `GET /api/requests/chat-unread-summary?requestId=LR-00001&requestId=LR-00002`.
 *   The route is declared as a flat literal (not nested under `:id`) so
 *   it cannot collide with the `:id` capture on the controller and so an
 *   all-scope actor's loaded-page-id batch fans into one aggregate
 *   query rather than per-row lookups.
 * @aiInvariant `requestId` accepts a single value, an array, OR a
 *   comma-separated string for transport-form parity with
 *   `RequestListQuerySchema.status`. Output is always normalized to a
 *   non-empty array of `LR-NNNNN` strings; the array MUST be ≤
 *   `REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS`.
 */
const RequestIdLiteralSchema = z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN');
const SingleRequestIdOrArraySchema = z.union([
  z.array(RequestIdLiteralSchema),
  RequestIdLiteralSchema.transform((s) => [s]),
  z
    .string()
    .transform((s) =>
      s
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
    )
    .pipe(z.array(RequestIdLiteralSchema).min(1)),
]);

export const RequestChatUnreadSummaryQuerySchema = z
  .object({
    requestId: SingleRequestIdOrArraySchema.pipe(
      z.array(RequestIdLiteralSchema).min(1).max(REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS),
    ),
  })
  .strict();
export type RequestChatUnreadSummaryQuery = z.infer<typeof RequestChatUnreadSummaryQuerySchema>;

/**
 * @aiPurpose per-request unread summary row.
 * @aiInvariant `unreadCount` excludes the current user's own messages
 *   and counts only messages strictly newer than
 *   `COALESCE(read_state.lastReadAt, rollout.baselineAt)`. The frontend
 *   caps display at `99+` (issue Resolved Decisions § Badge Semantics)
 *   — the wire still carries the true count so the aria-label can be
 *   accurate.
 * @aiInvariant `isMarkedUnread` is the user's personal flag; it does
 *   not move `lastReadAt` and is cleared only by explicit unmark.
 * @aiPii does NOT include actor names, request details, chat text, or
 *   exact read timestamps — strictly request id + integer count + bool.
 */
export const RequestChatUnreadSummaryItemSchema = z
  .object({
    requestId: RequestIdLiteralSchema,
    unreadCount: z.number().int().nonnegative(),
    isMarkedUnread: z.boolean(),
  })
  .strict();
export type RequestChatUnreadSummaryItem = z.infer<typeof RequestChatUnreadSummaryItemSchema>;

/**
 * @aiPurpose response envelope for the unread-summary endpoint. Flat
 *   array keyed by `requestId`; invisible request ids are omitted
 *   (existence is never disclosed) so the response surface is a
 *   subset of the requested ids.
 */
export const RequestChatUnreadSummaryResponseSchema = z.array(RequestChatUnreadSummaryItemSchema);
export type RequestChatUnreadSummaryResponse = z.infer<
  typeof RequestChatUnreadSummaryResponseSchema
>;

/**
 * @aiPurpose body for `POST /api/requests/:id/chat/read`. Carries the
 *   chronologically newest visible message id; the server derives the
 *   timestamp from the persisted row and advances `lastReadAt`
 *   monotonically via DB-level `GREATEST`. The client MUST NOT send a
 *   timestamp.
 * @aiInvariant `messageId` is a UUID — `RequestChatMessage.id` is
 *   `@default(uuid())` in Prisma.
 */
export const RequestChatReadBodySchema = z
  .object({
    messageId: z.string().uuid(),
  })
  .strict();
export type RequestChatReadBody = z.infer<typeof RequestChatReadBodySchema>;
